// src/services/exportExcel.js
import * as XLSX from "xlsx";

export function exporterRapportExcel({ tombola, billets, lots, params, options = {} }) {
  const { telephone = true, numeroBillet = true, benevole = true, gagnant = true } = options;
  const wb = XLSX.utils.book_new();
  const prixTicket = params?.prixTicket || 1;

  // ─── Onglet 1 : Récapitulatif ────────────────────────────────────────────────
  const totalBillets = billets.length;
  const recette = totalBillets * prixTicket;

  // Encaissements par méthode
  const parMethode = {};
  billets.forEach((b) => {
    const m = b.methodeEncaissement || "Non précisé";
    if (!parMethode[m]) parMethode[m] = { count: 0, total: 0 };
    parMethode[m].count++;
    parMethode[m].total += prixTicket;
  });

  const recapData = [
    ["RÉCAPITULATIF GÉNÉRAL"],
    [],
    ["Tombola", tombola?.nom || ""],
    ["Année", tombola?.annee || ""],
    ["Plan", tombola?.plan === "pro" ? "Pro ⭐" : "Starter"],
    [],
    ["BILLETS"],
    ["Total billets vendus", totalBillets],
    ["Recette totale (€)", recette.toFixed(2)],
    ["Prix par billet (€)", prixTicket],
    ["Nombre de lots", lots.length],
    [],
    ["ENCAISSEMENTS PAR MÉTHODE"],
    ["Méthode", "Billets", "Montant (€)", "%"],
    ...Object.entries(parMethode).map(([m, v]) => [
      m,
      v.count,
      v.total.toFixed(2),
      totalBillets > 0 ? `${Math.round((v.count / totalBillets) * 100)}%` : "0%",
    ]),
    [],
    ["TOTAL", totalBillets, recette.toFixed(2), "100%"],
  ];

  const ws1 = XLSX.utils.aoa_to_sheet(recapData);
  ws1["!cols"] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, ws1, "Récapitulatif");

  // ─── Onglet 2 : Liste des acheteurs ──────────────────────────────────────────
  const acheteurs = {};
  billets.forEach((b) => {
    const key = `${b.nom}_${b.telephone}`;
    if (!acheteurs[key]) {
      acheteurs[key] = {
        nom: b.nom || "",
        telephone: b.telephone || "",
        billets: [],
        encaissement: b.methodeEncaissement || "",
        benevole: b.saisiPar || "",
        gagnant: false,
      };
    }
    acheteurs[key].billets.push(b.numero);
    if (b.gagnant) acheteurs[key].gagnant = true;
  });

  const acheteursHeaders = ["Nom"];
  if (telephone) acheteursHeaders.push("Téléphone");
  if (numeroBillet) acheteursHeaders.push("Billets");
  acheteursHeaders.push("Nb billets", "Montant (€)", "Encaissement");
  if (benevole) acheteursHeaders.push("Bénévole");
  if (gagnant) acheteursHeaders.push("Gagnant");

  const acheteursData = [
    ["LISTE DES ACHETEURS"],
    [],
    acheteursHeaders,
    ...Object.values(acheteurs).map((a) => {
      const row = [a.nom];
      if (telephone) row.push(a.telephone);
      if (numeroBillet) row.push(a.billets.join(", "));
      row.push(a.billets.length, (a.billets.length * prixTicket).toFixed(2), a.encaissement);
      if (benevole) row.push(a.benevole);
      if (gagnant) row.push(a.gagnant ? "🏆 OUI" : "");
      return row;
    }),
  ];

  const ws2 = XLSX.utils.aoa_to_sheet(acheteursData);
  ws2["!cols"] = [{ wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 10 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, ws2, "Acheteurs");

  // ─── Onglet 3 : Ventes par bénévole ──────────────────────────────────────────
  const parBenevole = {};
  billets.forEach((b) => {
    const email = b.saisiPar || "Non assigné";
    if (!parBenevole[email]) parBenevole[email] = { count: 0, total: 0 };
    parBenevole[email].count++;
    parBenevole[email].total += prixTicket;
  });

  const benevoleData = [
    ["VENTES PAR BÉNÉVOLE"],
    [],
    ["Email bénévole", "Billets vendus", "Montant (€)", "%"],
    ...Object.entries(parBenevole)
      .sort((a, b) => b[1].count - a[1].count)
      .map(([email, v]) => [
        email,
        v.count,
        v.total.toFixed(2),
        totalBillets > 0 ? `${Math.round((v.count / totalBillets) * 100)}%` : "0%",
      ]),
    [],
    ["TOTAL", totalBillets, recette.toFixed(2), "100%"],
  ];

  const ws3 = XLSX.utils.aoa_to_sheet(benevoleData);
  ws3["!cols"] = [{ wch: 35 }, { wch: 15 }, { wch: 15 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, ws3, "Bénévoles");

  // ─── Onglet 4 : Lots ─────────────────────────────────────────────────────────
  const lotsData = [
    ["LISTE DES LOTS"],
    [],
    ["Rang", "Description", "Valeur", "Offert par", "Gagnant", "Billet gagnant"],
    ...lots.map((lot, i) => {
      const billetGagnant = billets.find((b) => b.lotGagne === lot.id);
      return [
        i + 1,
        lot.description || "",
        lot.valeur || "",
        lot.offertPar || "",
        billetGagnant ? `${billetGagnant.nom || ""}` : "",
        billetGagnant ? billetGagnant.numero : "",
      ];
    }),
  ];

  const ws4 = XLSX.utils.aoa_to_sheet(lotsData);
  ws4["!cols"] = [{ wch: 8 }, { wch: 35 }, { wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, ws4, "Lots");

  // ─── Téléchargement ──────────────────────────────────────────────────────────
  const nomFichier = `TomboLive_${tombola?.nom || "Rapport"}_${tombola?.annee || new Date().getFullYear()}.xlsx`;
  XLSX.writeFile(wb, nomFichier);
}
